import React, {useContext, useState, useMemo} from 'react';
import flow from 'lodash.flow';
import ActionsList from "../ActionsList/ActionsList";
import AppContext, {ACTION_ISOLATE_ITEM_DISABLED, INTRO_MOVIE_FINISHED} from "../../store";
import {
  applyProperties,
  applyEvents,
  applyIconDataNew,
} from "./helpers";
import EventsCollection from '../../helpers/EventsCollection';
import {
  ACTION_DOTS,
  ACTION_POWER,
  ACTION_OPEN,
  ACTION_INSPECT,
  ACTION_RGB,
  ACTION_RESET_CAMERA,
  EFFECT_ID_RAINBOW,
  EFFECT_ID_RAIN,
  EFFECT_ID_COLOR_ONE,
  EFFECT_ID_COLOR_TWO,
  EFFECT_ID_COLOR_THREE,
  PRESET_DOTS,
  PRESET_RESET_CONFIG,
  PRESET_RESET_CAMERA,
  PRESET_RAIN,
  PRESET_COLOR_ONE,
  PRESET_COLOR_TWO,
  PRESET_COLOR_THREE,
  PRESET_RGB,
  PRESET_POWER,
  PRESET_OPEN,
  PRESET_INSPECT,
  PRESET_RAINBOW,
  SYSTEM_ID_RGB,
  SYSTEM_ID_DOTS,
  SYSTEM_ID_ICUE,
  ACTION_STUDIO_MODE,
  PRESET_INFO,
  PRESET_STUDIO_MODE,
  SYSTEM_ID_TOGGLE_DESK_CAMERA,
  ACTION_CALL_INFO,
  ACTION_RESET_CONFIG, SYSTEM_ID_TOGGLE_DOOR, SYSTEM_ID_TOGGLE_POWER, SYSTEM_ID_TOGGLE_INSPECT,
  PRESET_SOUND,
  ACTION_SOUND,
  SYSTEM_ID_SOUND,
  ACTION_FULL_SCREEN,
  PRESET_FULL_SCREEN,
} from "../../contants/presets";
import useConfiguratorMount from "./hooks/useConfiguratorMount";
import ConfiguratorHolder from "./ConfiguratorHolder";
import classNames from "classnames";
import {PROPERTY_ACTIVE, PROPERTY_CLASS_NAME, PROPERTY_VISIBLE} from "../../contants/common";
import useStreamActionsSingle from "../../hooks/useStreamActionsSingle";
import useViewMode from "../../hooks/useViewMode";
import useSound from "./hooks/useSound";
import {openFullscreen} from "../../helpers";

const fullscreenEnabled = document.fullscreenEnabled;

const presetsMain = [
  PRESET_INSPECT,
  PRESET_OPEN,
  PRESET_POWER,
  PRESET_DOTS,
  PRESET_RAINBOW,
  PRESET_RAIN,
  PRESET_COLOR_ONE,
  PRESET_COLOR_TWO,
  PRESET_COLOR_THREE,
  PRESET_RGB,
  PRESET_RESET_CONFIG,
  ...(fullscreenEnabled ? [PRESET_FULL_SCREEN] : [])
];

const presetsSub = [
  PRESET_INFO,
  PRESET_STUDIO_MODE,
  PRESET_RESET_CAMERA,
  PRESET_SOUND,
];

const invisibleFirst = [
  ACTION_INSPECT,
  ACTION_OPEN,
  ACTION_POWER,
];

const invisibleSecond = [
  EFFECT_ID_RAINBOW,
  EFFECT_ID_RAIN,
  EFFECT_ID_COLOR_ONE,
  EFFECT_ID_COLOR_TWO,
  EFFECT_ID_COLOR_THREE,
];

const itemsWithLeftBorder = [
  ACTION_SOUND,
  ACTION_STUDIO_MODE,
  ACTION_RESET_CAMERA,
  ACTION_RGB,
  ACTION_RESET_CONFIG,
  EFFECT_ID_RAINBOW,
  ...(fullscreenEnabled ? [ACTION_FULL_SCREEN] : []),
];

const CLASS_BORDERED = 'bordered';
const checkIsBordered = id => ~itemsWithLeftBorder.indexOf(id);
const applyBorderedClass = (acc, id) => {
  const isBordered = checkIsBordered(id);
  if(isBordered) {
    acc[id] = () => CLASS_BORDERED;
  }
  return acc;
}

const getIconSize = initialProps => {
  return ({id}) => {
    if(~[
      EFFECT_ID_RAINBOW,
      EFFECT_ID_RAIN,
      EFFECT_ID_COLOR_ONE,
      EFFECT_ID_COLOR_TWO,
      EFFECT_ID_COLOR_THREE,
    ].indexOf(id)) {
      return {...initialProps, width: 25, boxSize: 34};
    } else if (~[
      ACTION_POWER,
      ACTION_OPEN,
      ACTION_INSPECT
    ].indexOf(id)) {
      return {...initialProps, width: 18};
    } else if (ACTION_CALL_INFO === id) {
      return {...initialProps, width: 22};
    } else if (ACTION_STUDIO_MODE === id) {
      return {...initialProps, width: 21};
    } else if (ACTION_RGB === id) {
      return {...initialProps, width: 22};
    } else if (ACTION_DOTS === id) {
      return {...initialProps, width: 16};
    } else if (ACTION_RESET_CAMERA === id) {
      return {...initialProps, width: 22};
    } else if (ACTION_RESET_CONFIG === id) {
      return {...initialProps, width: 16}
    } else if (ACTION_FULL_SCREEN === id) {
      return {...initialProps, width: 16}
    }
    return initialProps;
  }
}

export default function ConfiguratorMobile() {
  const {
    streamStore,
    setStreamStore,
    isInteractionsPortalOpen,
    toggleInteractionsPortal,
    isConfiguratorAvailable,
  } = useContext(AppContext);
  
  const emitter = useStreamActionsSingle();
  const {isInspectView, isIsolateView} = useViewMode();
  const {
    sizeContainerRef,
    containerRef,
    videoRef,
    onPresetClickHandler,
    audioRef, /*Audio functionality*/
  } = useConfiguratorMount();
  const {
    isSoundOn,
    toggleSound,
  } = useSound();

  const [visible, setVisible] = useState({
    [ACTION_DOTS]: false,
    [ACTION_RGB]: false,
  });
  
  const invisiblePresets = useMemo(() => ([
    ...(visible[ACTION_DOTS] ? [] : invisibleFirst),
    ...(visible[ACTION_RGB] ? [] : invisibleSecond),
  ]), [visible]);
  
  const eventsCollection = useMemo(() => {
    const _ec = new EventsCollection();
  
    _ec
      .add(ACTION_CALL_INFO, {
        onClick: toggleInteractionsPortal,
      })
      .add(ACTION_FULL_SCREEN, {
        onClick: () => openFullscreen(containerRef.current),
      })
      .add([
          ACTION_RGB,
          ACTION_DOTS,
          ACTION_OPEN,
          ACTION_POWER,
          ACTION_STUDIO_MODE,
          ACTION_RESET_CAMERA,
          ACTION_RESET_CONFIG,
          ...invisibleSecond
        ],
        {
          onClick: [onPresetClickHandler],
        }
      )
      .add([
        ACTION_INSPECT
      ], {
        onClick: (el) => {
          /*Custom behavior*/
          if(isIsolateView) {
            if(emitter) {
              emitter.client.emitUIInteraction({
                [ACTION_ISOLATE_ITEM_DISABLED]: 1
              })
            }
          } else {
            if(emitter) {
              setStreamStore({
                [SYSTEM_ID_TOGGLE_DESK_CAMERA]: 0
              })
            }
            onPresetClickHandler(el);
          }
        }
      })
      .add([ACTION_RGB, ACTION_DOTS], {
        onClick: el => {
          const {id} = el;
          const disabledId = id === ACTION_RGB
            ? ACTION_RGB
            : ACTION_DOTS;
          setVisible({
            [disabledId]: false,
            [id]: !visible[id],
          });
        }
      })
      .add([ACTION_INSPECT, ACTION_OPEN, ACTION_POWER], {
        onClick: el => {
          setVisible({
            ...visible,
            [ACTION_DOTS]: false,
          });
        }
      })
      .add([
        ACTION_SOUND,
      ], {
        onClick: () => toggleSound(!isSoundOn),
      })
    
    return _ec;
  }, [
    isSoundOn,
    toggleInteractionsPortal,
    onPresetClickHandler,
    visible,
    setVisible
  ])
  
  const preparedPresetsMemoized = useMemo(() => {
    return presetsMain.map(el => flow([
      applyProperties({
        [PROPERTY_VISIBLE]: {
          conditions: invisiblePresets,
          isNegation: true
        },
        [PROPERTY_ACTIVE]: {
          conditions: {
            [SYSTEM_ID_DOTS]: () => visible[ACTION_DOTS],
            [SYSTEM_ID_RGB]: () => visible[ACTION_RGB],
            [SYSTEM_ID_ICUE]: el => streamStore[SYSTEM_ID_ICUE] === el.id,
            [SYSTEM_ID_TOGGLE_DOOR]: () => streamStore[SYSTEM_ID_TOGGLE_DOOR],
            [SYSTEM_ID_TOGGLE_POWER]: () => streamStore[SYSTEM_ID_TOGGLE_POWER],
            [SYSTEM_ID_TOGGLE_INSPECT]: () => {
              return (!!streamStore[el.action.id] && isInspectView) || isIsolateView
            },
          },
          getBy: 'action.id'
        },
        [PROPERTY_CLASS_NAME]: {
          conditions: {
            ...[
              ACTION_RGB,
              ACTION_RESET_CONFIG,
              EFFECT_ID_RAINBOW,
              ACTION_FULL_SCREEN,
            ].reduce(applyBorderedClass, {}),
          },
        }
      }),
      applyIconDataNew(getIconSize({width: 26, height: 20, boxSize: 40})),
      applyEvents(eventsCollection, 'id'),
    ])(el))
  }, [
    streamStore,
    visible,
    invisiblePresets,
    eventsCollection,
  ])
  
  const preparedSubActionsMemoized = useMemo(() => {
    return presetsSub.map(el => flow([
      applyProperties({
        [PROPERTY_VISIBLE]: {
          conditions: {
            [ACTION_STUDIO_MODE]: () => isIsolateView,
          },
          isNegation: true,
        },
        [PROPERTY_ACTIVE]: {
          conditions: {
            [ACTION_STUDIO_MODE]: () => streamStore[SYSTEM_ID_TOGGLE_DESK_CAMERA],
            [ACTION_CALL_INFO]: () => isInteractionsPortalOpen,
            [ACTION_SOUND]: () => isSoundOn,
          }
        },
        [PROPERTY_CLASS_NAME]: {
          conditions: {
            ...[
              ACTION_SOUND,
              ACTION_STUDIO_MODE,
              ACTION_RESET_CAMERA,
            ].reduce(applyBorderedClass, {}),
          },
        }
      }),
      applyIconDataNew([
        getIconSize({width: 16, boxSize: 40}),
      ]),
      applyEvents(eventsCollection, 'id'),
    ])(el))
  }, [
    streamStore,
    isInteractionsPortalOpen,
    eventsCollection,
    streamStore[SYSTEM_ID_SOUND]
  ])
  
  return (
    <ConfiguratorHolder
      sizeContainerRef={sizeContainerRef}
      containerRef={containerRef}
      videoRef={videoRef}
      audioRef={audioRef}
    >
      <>
        <ActionsList
          className={classNames('actions-sub', {
            hidden: !isConfiguratorAvailable
          })}
          items={preparedSubActionsMemoized}
        />
        
        <ActionsList
          className={classNames('presets-main', {
            'expanded-rgb': visible[ACTION_RGB],
            'expanded-dots': visible[ACTION_DOTS],
            hidden: !isConfiguratorAvailable
          })}
          items={preparedPresetsMemoized}
        />
      </>
    </ConfiguratorHolder>
  )
}
